import { Center } from "@mantine/core";
import { Notifications, notifications } from "@mantine/notifications";
import { CloseCircle, InfoCircle, Receipt21, TickCircle } from "iconsax-react";
import { createContext, ReactNode, useContext } from "react";
import { client_selected } from "../client";
import { useTranslation } from "react-i18next";

type NotificationProps = {
  id?: string;
  title?: string;
  message?: string;
};

export interface updateNotificationControlProps extends NotificationProps {
  isSuccess: boolean;
}

export interface notificationControlProps extends NotificationProps {
  type: "success" | "danger" | "warning" | "check" | "default" | "loading";
}
interface NotificationControlContextProps {
  notificationControl: (notificationData: notificationControlProps) => void;
  updateNotificationControl: (
    updateNotificationData: updateNotificationControlProps
  ) => void;
}

type ProviderProps = {
  children: ReactNode;
};

export const NotificationControlContext =
  createContext<NotificationControlContextProps>(
    {} as NotificationControlContextProps
  );

export const NotificationControlProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const notificationVariants = (theme = client_selected) => ({
    default: {
      id: "default",
      color: theme.colors.primary[1],
      bg: theme.colors.primaryOpacity[1],
      icon: null,
      title: t("global.notification.default.title"),
      message: t("global.notification.default.message"),
    },
    check: {
      id: "check",
      color: "primaryOpacity.2",
      bg: theme.colors.primaryOpacity[1],
      icon: (
        <Center pos="relative">
          <Center>
            <Receipt21 size="28" color={theme.colors.primary[1]} />
          </Center>
          <Center
            pos="absolute"
            right="-1px"
            bottom="-1px"
            style={{ zIndex: 9 }}
          >
            <TickCircle size="13" color={theme.colors.basic[0]} />
          </Center>
          <Center
            pos="absolute"
            right="-1px"
            bottom="-1px"
            style={{ zIndex: 10 }}
          >
            <TickCircle
              size="13"
              color={theme.colors.complementary[0]}
              variant="Bold"
            />
          </Center>
        </Center>
      ),
      title: t("global.notification.check.title"),
      message: t("global.notification.check.message"),
    },
    danger: {
      id: "danger",
      color: "alertsOpacity.0",
      bg: theme.colors.alertsOpacity[0],
      icon: (
        <CloseCircle size="28" color={theme.colors.alerts[0]} variant="Bold" />
      ),
      title: t("global.notification.danger.title"),
      message: t("global.notification.danger.message"),
    },
    warning: {
      id: "warning",
      color: "alertsOpacity.1",
      bg: theme.colors.alertsOpacity[1],
      icon: (
        <InfoCircle size="28" color={theme.colors.alerts[1]} variant="Bold" />
      ),
      title: t("global.notification.warning.title"),
      message: t("global.notification.warning.message"),
    },
    success: {
      id: "success",
      color: "alertsOpacity.2",
      bg: theme.colors.alertsOpacity[2],
      icon: (
        <TickCircle
          size="28"
          color={theme.colors.complementary[0]}
          variant="Bold"
        />
      ),
      title: t("global.notification.success.title"),
      message: t("global.notification.success.message"),
    },
    loading: {
      id: "loading",
      color: theme.colors.primary[1],
      bg: theme.colors.primaryOpacity[1],
      icon: null,
      title: t("global.notification.loading.title"),
      message: t("global.notification.loading.message"),
    },
  });

  function notificationControl(notificationData: notificationControlProps) {
    const theme = client_selected;
    const variant = notificationVariants(theme)[notificationData.type];
    return notifications.show({
      id: notificationData.id ?? variant.id,
      title: notificationData.title ?? variant.title,
      message: notificationData.message ?? variant.message,
      icon: variant.icon,
      color: variant.color,
      style: { backgroundColor: variant.bg },
      loading: notificationData.type === "loading" ? true : undefined,
      autoClose: notificationData.type === "loading" ? false : undefined,
    });
  }

  function updateNotificationControl(
    updateNotificationData: updateNotificationControlProps
  ) {
    const theme = client_selected;
    const variant =
      notificationVariants(theme)[
        updateNotificationData.isSuccess ? "success" : "danger"
      ];

    return notifications.update({
      id: updateNotificationData.id ?? variant.id,
      title: updateNotificationData.title ?? variant.title,
      message: updateNotificationData.message ?? variant.message,
      icon: variant.icon,
      color: variant.color,
      style: { backgroundColor: variant.bg },
      autoClose: 2000,
    });
  }

  const value = { notificationControl, updateNotificationControl };

  return (
    <NotificationControlContext.Provider value={value}>
      <Notifications />
      {children}
    </NotificationControlContext.Provider>
  );
};

export function useNotificationControl(): NotificationControlContextProps {
  const context = useContext(NotificationControlContext);

  return context;
}
